import { useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import { useSnackbar } from "notistack";

import { v4 as uuidv4 } from "uuid";

import { RootRef, makeStyles } from "../../utils/barrels/material-ui-core";
import { CloudUploadIcon } from "../../utils/barrels/material-ui-icons";
import { parseAcceptedFiles } from "../../utils/parseAcceptedFiles";
import GridContainer from "../Grid/GridContainer";
import GridItem from "../Grid/GridItem";

function UploadFile(props) {
	const { enqueueSnackbar } = useSnackbar();
	const { t } = useTranslation();

	const [fileName, setFileName] = useState("");
	const [isFileUploadDisabled, setIsFileUploadDisabled] = useState(false);
	const [borderColor, setBorderColor] = useState("#bbb");
	const validationFailedRef = useRef(false);

	useEffect(() => {
		if (props.resetFileNames) {
			setFileName("");
		}
	}, [props.resetFileNames]);

	useEffect(() => {
		if (props.fileName) {
			setFileName(props.fileName);
		}
	}, [props.fileName]);

	useEffect(() => {
		if (props.uploadState === "error") {
			setBorderColor("red");
		} else if (props.uploadState === "success") {
			setBorderColor("green");
		} else {
			setBorderColor("");
		}
	}, [props.uploadState]);

	const handleFileSuccess = () => {
		setBorderColor("green");
		enqueueSnackbar(t("file_upload_successful"), {
			variant: "success",
			autoHideDuration: 3000,
		});
	};

	const handleFileError = (err) => {
		setBorderColor("red");
		const error = err[0].errors[0].message;
		enqueueSnackbar(error, {
			variant: "error",
			autoHideDuration: 3000,
		});
	};

	const uploadFile = (uniqueName, fileType, droppedFile) => {
		setIsFileUploadDisabled(true);
		setBorderColor("#bbb");
		props
			.generateSignedUrl(uniqueName, fileType, props.url)
			.then((res) => {
				const { url, fields } = res;
				props
					.uploadFiletoS3(droppedFile, url, fields)
					.then((status) => {
						if (status) {
							setIsFileUploadDisabled(false);
							props.uploadFunction(fields.key);
						}
					})
					.catch(() => {
						setIsFileUploadDisabled(false);
						enqueueSnackbar(t("something_went_wrong"), {
							variant: "error",
							autoHideDuration: 3000,
						});
					});
			})
			.catch(() => {
				setIsFileUploadDisabled(false);
				enqueueSnackbar(t("something_went_wrong"), {
					variant: "error",
					autoHideDuration: 3000,
				});
			});
	};

	const dropFile = (file) => {
		const droppedFile = file[0];
		validationFailedRef.current = false;
		if (droppedFile) {
			const fileType = droppedFile.name.split(".").slice(-1)[0];
			if (props.preUploadValidator) {
				const validationError = props.preUploadValidator(droppedFile, fileType);
				if (validationError) {
					validationFailedRef.current = true;
					setBorderColor("red");
					setFileName("");
					props.setFileName && props.setFileName("");
					props.onValidationError && props.onValidationError();
					enqueueSnackbar(validationError, {
						variant: "error",
						autoHideDuration: 3000,
					});
					return;
				}
			}
			setBorderColor("green");
			const name = droppedFile.name;
			setFileName(name);
			props.setFileName && props.setFileName(name);
			const uniqueName = uuidv4(name);
			uploadFile(uniqueName, fileType, droppedFile);
		}
	};

	const { getRootProps, getInputProps } = useDropzone({
		multiple: false,
		accept: parseAcceptedFiles(props.acceptedFiles),
		onDrop: (files) => dropFile(files),
		onDropAccepted: (e) => {
			if (validationFailedRef.current) {
				return;
			}
			handleFileSuccess(e);
		},
		onDropRejected: (e) => handleFileError(e),
	});
	const { ref } = getRootProps();

	const styles = {
		uploadBtnContainer: {
			display: "flex",
			padding: "4px 0",
			flexDirection: "row",
		},
		uploadBoxContent: {
			display: "flex",
			placeItems: "center",
			padding: "12px",
			flexDirection: "column",
			borderRadius: "8px",
			border: `1px dashed ${borderColor}`,
			cursor: "pointer",
			maxWidth: "300px",
		},
		inputfile: {
			width: "0.1px",
			height: "0.1px",
			opacity: "0",
			overflow: "hidden",
			position: "absolute",
			zIndex: -1,
		},
		headingContainer: {
			display: "flex",
			justifyContent: "space-between",
			width: "100%",
		},
		heading: {
			fontSize: "14px",
		},
		secondaryHeading: {
			fontSize: "14px",
		},
		accordionContainer: {
			marginTop: "16px",
		},
		downloadLink: {
			fontSize: "12px",
			padding: "8px",
		},
		btnContainer: {
			margin: "8px",
			alignSelf: "center",
		},
		helperText: {
			margin: "4px 0",
		},
		boxMsg: {
			textAlign: "center",
			fontSize: "14px",
			padding: "0",
			margin: "0",
		},
		"@media (max-width: 600px)": {
			uploadBtnContainer: {
				flexDirection: "column",
			},
		},
	};

	const useStyles = makeStyles(styles);
	const classes = useStyles();
	return (
		<GridContainer>
			<GridItem xs={12} sm={12} md={12}>
				<div className={classes.uploadBtnContainer}>
					<section className={classes.uploadBtnContainer}>
						<RootRef rootRef={ref}>
							<div {...getRootProps({ className: "dropzone" })}>
								<input {...getInputProps()} disabled={isFileUploadDisabled} />
								<div>
									{fileName ? (
										<div className={classes.uploadBoxContent}>
											{props.showIcon ? (
												<CloudUploadIcon style={{ fontSize: 48 }} />
											) : (
												""
											)}
											<p className={classes.boxMsg}>{fileName}</p>
										</div>
									) : (
										<div className={classes.uploadBoxContent}>
											{props.showIcon ? (
												<CloudUploadIcon style={{ fontSize: 48 }} />
											) : (
												""
											)}
											<p className={classes.boxMsg}>
												{t("upload")} {props.fileType}
											</p>
										</div>
									)}
								</div>
							</div>
						</RootRef>
						<div className={classes.btnContainer} />
					</section>
				</div>
			</GridItem>
		</GridContainer>
	);
}

export default UploadFile;
